import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Steps from "../../components/steps/Steps";
import clint_image_icon from "../../assets/images/clint_image_icon.svg";
import { createClient, resetStatus } from "../../store/slices/userSlice";
import {
  fetchCountries,
  fetchStates,
  fetchCities,
} from "../../store/slices/locationSlice";

import StatusPopup from "../../components/popups/StatusPopup";
import EditSuccessPopup from "../../components/popups/EditSuccessPopup";
import CountryCreatePopup from "../../components/popups/CountryCreatePopup";
import StateCreatePopup from "../../components/popups/StateCreatePopup";
import CityCreatePopup from "../../components/popups/CityCreatePopup";

const AddNewClint = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const location = useLocation();

  const fromPath = location.state?.from || "/dashboard";

  const { isSuccess, errorMessage } = useSelector((state) => state.user);

  const {
    countries = [],
    states = [],
    cities = [],
  } = useSelector((state) => state.location || {});

  // IMAGE
  const [profileImage, setProfileImage] = useState(null);

  const [preview, setPreview] = useState(null);

  // SUCCESS
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const [clientName, setClientName] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  // LOCATION
  const [showCountryPopup, setShowCountryPopup] = useState(false);
  const [showStatePopup, setShowStatePopup] = useState(false);
  const [showCityPopup, setShowCityPopup] = useState(false);
  const [locationFormValues, setLocationFormValues] = useState({});
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [selectedStateId, setSelectedStateId] = useState("");

  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  // SUCCESS
  useEffect(() => {
    if (isSuccess && isFormSubmitting) {
      setShowSuccessPopup(true);
      setIsLoading(false);
      setIsFormSubmitting(false);
    }
  }, [isSuccess, isFormSubmitting]);

  // FETCH DATA
  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch]);

  // HIDE FOOTER
  useEffect(() => {
    const footer = document.querySelector("footer");

    if (footer) {
      footer.style.display = "none";
    }

    return () => {
      if (footer) {
        footer.style.display = "";
      }
    };
  }, []);

  const handleOpenLocationPopup = (type, data = {}) => {
    // RESET OLD SUCCESS STATE
    dispatch(resetStatus());

    if (type === "country") {
      setShowCountryPopup(true);
    }

    if (type === "state") {
      setSelectedCountryId(data.countryId || "");

      setShowStatePopup(true);
    }

    if (type === "city") {
      setSelectedStateId(data.stateId || "");

      setShowCityPopup(true);
    }
  };

  const handleCountryCreated = (country) => {
    dispatch(fetchCountries());
    setTimeout(() => {
      setLocationFormValues((prev) => ({
        ...prev,
        Country: String(country.id),
        CountryId: String(country.id),
        State: "",
        StateId: "",
        City: "",
        CityId: "",
      }));
    }, 300);
  };

  const handleStateCreated = (state) => {
    setLocationFormValues((prev) => ({
      ...prev,
      State: String(state.id),
      StateId: String(state.id),
      City: "",
      CityId: "",
    }));
  };

  const handleCityCreated = (city) => {
    dispatch(fetchCities(city.stateId));
    setTimeout(() => {
      setLocationFormValues((prev) => ({
        ...prev,
        City: String(city.id),
        CityId: String(city.id),
      }));
    }, 300);
  };

  // SUBMIT
  const handleFormSubmit = (data) => {
    setIsFormSubmitting(true);
    const formData = new FormData();

    setClientName(data["Client Name"]);
    formData.append("name", data["Client Name"]);
    formData.append("email", data["Email Address"]);
    formData.append("phone", data["Phone Number"]);
    formData.append("cityId", data["CityId"] || data["City"]);
    formData.append("postalCode", data["ZIP Code"]);
    formData.append("addressLine1", data["Address"]);
    formData.append("password", data["Password"]);

    // IMAGE
    if (profileImage) {
      formData.append("file", profileImage);
    }

    setIsLoading(true);

    dispatch(createClient(formData))
      .unwrap()
      .catch(() => {
        setIsLoading(false);
      });
  };

  // SUCCESS CLOSE
  const handleSuccessPopupClose = () => {
    setShowSuccessPopup(false);

    dispatch(resetStatus());

    navigate("/client");
  };

  // STEPS
  const steps = [
    {
      stepId: "Step1",
      title: "Client Information",
      subtitle: "",
      showImage: true,
      imageSrc: preview || clint_image_icon,
      fields: [
        {
          label: "Client Name",
          placeholder: "Enter client name",
          required: true,
        },
        {
          label: "Email Address",
          type: "email",
          placeholder: "Enter email address",
          required: true,
        },
        {
          label: "Phone Number",
          type: "tel",
          placeholder: "Enter phone number",
          required: true,
          pattern: "[0-9]*",
          inputMode: "numeric",
          maxLength: 10,
        },
        {
          label: "Date of Birth",
          type: "date",
          placeholder: "Select date",
        },
        {
          label: "Password",
          type: "password",
          placeholder: "Enter password",
          required: true,
        },
      ],
      imageRequired: false,
      headings: [""],
      showCheckbox: false,
    },
    {
      stepId: "Step2",
      title: "Client Address",
      subtitle: "",
      showImage: false,
      fields: [
        {
          label: "Country",
          type: "dropdown",
          placeholder: "Select Country",
          required: true,
          options: [
            ...(countries?.map((c) => ({
              value: String(c.id),
              label: c.name,
            })) || []),
            { value: "__add_new__", label: "Add new country" },
          ],
        },
        {
          label: "State",
          type: "dropdown",
          placeholder: "Select State",
          required: true,
          options: [
            ...(states?.map((s) => ({
              value: String(s.id),
              label: s.name,
            })) || []),
            { value: "__add_new__", label: "Add new state" },
          ],
        },
        {
          label: "City",
          type: "dropdown",
          placeholder: "Select City",
          required: true,
          options: [
            ...(cities?.map((c) => ({
              value: String(c.id),
              label: c.name,
            })) || []),
            { value: "__add_new__", label: "Add new city" },
          ],
        },
        {
          label: "ZIP Code",
          type: "text",
          placeholder: "Enter ZIP Code",
          required: true,
        },
        {
          label: "Address",
          type: "text",
          placeholder: "Enter Address",
          required: true,
        },
      ],

      headings: [" "],

      showCheckbox: false,
    },
  ];

  return (
    <>
      <div className="add_new_clint">
        <div className="container-fluid h-100">
          <div className="row h-100">
            {/* LEFT */}
            <div className="left-content col-md-6 bg_light_grey h-100">
              <div className="content pt_80">
                <h4 className="mb_30">Start Smart. Add Your Next Client.</h4>

                <p>
                  Quickly create and configure a new client profile, assign
                  companies, and set payroll preferences — all in a few clicks.
                </p>
              </div>
            </div>

            {/* RIGHT */}
            <div className="col-md-6 bg_white h-100 px-0">
              {/* IMAGE */}
              <input
                type="file"
                accept="image/*"
                id="profileImageInput"
                style={{
                  display: "none",
                }}
                onChange={(e) => {
                  const file = e.target.files[0];

                  if (file) {
                    setProfileImage(file);

                    setPreview(URL.createObjectURL(file));
                  }
                }}
              />

              {/* STEPS */}
              <Steps
                steps={steps}
                initialValues={{
                  ...locationFormValues,
                }}
                onSubmit={handleFormSubmit}
                onImageClick={() =>
                  document.getElementById("profileImageInput").click()
                }
                imageSelected={!!profileImage}
                loading={isLoading}
                onOpenLocationPopup={handleOpenLocationPopup}
              />
            </div>
          </div>
        </div>
      </div>

      {/* COUNTRY POPUP */}
      <CountryCreatePopup
        show={showCountryPopup}
        onHide={() => setShowCountryPopup(false)}
        onSuccess={handleCountryCreated}
      />

      <StateCreatePopup
        show={showStatePopup}
        onHide={() => setShowStatePopup(false)}
        onSuccess={handleStateCreated}
        selectedCountryId={selectedCountryId}
      />

      <CityCreatePopup
        show={showCityPopup}
        onHide={() => setShowCityPopup(false)}
        onSuccess={handleCityCreated}
        selectedStateId={selectedStateId}
      />

      {/* ERROR */}
      <StatusPopup
        show={errorMessage !== null}
        type="error"
        title="Error"
        message={errorMessage}
        handleClose={() => dispatch(resetStatus())}
      />

      {/* SUCCESS */}
      <EditSuccessPopup
        show={showSuccessPopup}
        onHide={handleSuccessPopupClose}
        entityType="client"
        operationType="add"
        title="Client Created Successfully"
      />
    </>
  );
};

export default AddNewClint;
